import type * as T from "../types";
import type { components } from "./schema";

type Schemas = components["schemas"];
export type PartySummaryWire = Schemas["PartySummary"];
export type PartyWire = Schemas["Party"];
export type PartyStandingWire = Schemas["PartyStanding"];
export type PartyStatementWire = Schemas["PartyStatement"];
export type SupplierReconciliationWire = Schemas["SupplierReconciliation"];
export type AccountWire = Schemas["ChartAccount"];
export type BankAccountWire = Schemas["BankAccountBalance"];
export type CompanyWire = Schemas["CompanyProfile"];
export type FiscalYearWire = Schemas["FiscalYearSummary"];
export type NumberingWire = Schemas["NumberingSettings"];
export type TaxCodeWire = Schemas["TaxCodeSummary"];
export type JournalPageWire = Schemas["ManualJournalPage"];
export type JournalWire = Schemas["ManualJournalView"];

export function toJournal(wire: JournalWire): T.Journal {
  return {
    id: wire.id,
    number: wire.number ?? null,
    date: wire.date,
    currency: wire.currencyCode,
    narration: wire.narration,
    status: wire.status as T.JournalStatus,
    isAdjusting: wire.isAdjusting,
    reversesNextYear: wire.reversesNextYear,
    createdAt: wire.createdAt,
    postedAt: wire.postedAt ?? null,
    lines: wire.lines.map((line) => ({
      accountId: line.accountId,
      accountCode: line.accountCode,
      accountName: line.accountName,
      partyId: line.partyId ?? null,
      memo: line.memo ?? null,
      debit: String(line.debit),
      credit: String(line.credit),
    })),
  };
}

export function fromJournalDraft(draft: T.JournalDraft) {
  return {
    date: draft.date,
    currencyCode: draft.currency,
    narration: draft.narration,
    isAdjusting: draft.isAdjusting,
    reversesNextYear: draft.reversesNextYear,
    lines: draft.lines.map((line) => ({
      accountId: line.accountId,
      partyId: line.partyId,
      memo: line.memo || null,
      debit: line.debit,
      credit: line.credit,
    })),
  };
}

export const accountGroup = (type: Schemas["AccountType"]): T.AccountGroup => ACCOUNT_GROUP[type];

const ACCOUNT_GROUP: Record<Schemas["AccountType"], T.AccountGroup> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  income: "Income",
  expense: "Expenses",
};

const YEAR_STATUS: Record<Schemas["PeriodStatusCode"], T.FiscalYear["status"]> = {
  open: "open",
  softClosed: "locked",
  hardClosed: "closed",
};

function money(value: unknown): T.Money {
  return Number(value).toFixed(2);
}

export function toPartyStanding(wire: PartyStandingWire): T.PartyStanding {
  return {
    asAt: wire.asAt,
    fiscalYearStart: wire.fiscalYearStart ?? null,
    bucketDays: wire.bucketDays.map(Number),
    buckets: wire.buckets.map(money),
    outstanding: money(wire.outstanding),
    overdue: money(wire.overdue),
    oldestOverdueDays: wire.oldestOverdueDays == null ? null : Number(wire.oldestOverdueDays),
    settledThisYear: money(wire.settledThisYear),
  };
}

export type BankReconciliationWire = Schemas["Reconciliation"];
export type StatementMatchWire = Schemas["MatchedStatementLine"];
export type MatchCandidateWire = Schemas["MatchCandidate"];
export type StatementImportWire = Schemas["StatementImportResult"];

const balanceOrNull = (value: unknown) => (value == null ? null : money(value as string));

export function toBankReconciliation(wire: BankReconciliationWire): T.BankReconciliation {
  return {
    asAt: wire.asAt,
    ledgerBalance: money(wire.ledgerBalance),
    statementBalance: balanceOrNull(wire.statementBalance),
    unmatchedOnStatement: money(wire.unmatchedOnStatement),
    unmatchedInLedger: money(wire.unmatchedInLedger),
    difference: balanceOrNull(wire.difference),
    onStatementOnly: wire.onStatementOnly.map((line) => ({
      id: line.id,
      date: line.valueDate,
      description: line.description,
      amount: money(line.amount),
      reference: line.externalId ?? null,
    })),
    inLedgerOnly: wire.inLedgerOnly.map((movement) => ({
      journalId: movement.journalId,
      journalLineId: movement.journalLineId,
      documentType: movement.documentType,
      number: movement.documentNumber,
      date: movement.postingDate,
      amount: money(movement.amount),
      memo: movement.memo ?? null,
    })),
  };
}

export function toReconciliationLock(wire: Schemas["ReconciliationLock"]): T.ReconciliationLock {
  return {
    id: wire.id,
    asAt: wire.asAt,
    statementBalance: money(wire.statementBalance),
    ledgerBalance: money(wire.ledgerBalance),
    ledgerBalanceNow: money(wire.ledgerBalanceNow),
    lineCount: Number(wire.lineCount),
    lockedAt: wire.lockedAt,
    reopenedAt: wire.reopenedAt ?? null,
  };
}

export function toStatementMatch(wire: StatementMatchWire): T.MatchedStatementLine {
  return {
    matchId: wire.matchId,
    lineId: wire.statementLineId,
    date: wire.valueDate,
    description: wire.description,
    amount: money(wire.amount),
    reference: wire.externalId ?? null,
    documentType: wire.documentType,
    number: wire.documentNumber,
    postingDate: wire.postingDate,
  };
}

export function toMatchCandidate(wire: MatchCandidateWire) {
  return {
    journalId: wire.journalId,
    journalLineId: wire.journalLineId,
    documentType: wire.documentType,
    number: wire.documentNumber,
    date: wire.postingDate,
    amount: money(wire.amount),
    memo: wire.memo ?? null,
    daysApart: Number(wire.daysApart),
  };
}

export function toStatementImport(wire: StatementImportWire): T.StatementImportResult {
  return {
    linesImported: Number(wire.linesImported),
    problems: wire.problems.map((problem) => ({
      lineNumber: Number(problem.lineNumber),
      column: problem.column,
      message: problem.message,
    })),
    suspectedRepeats: wire.suspectedRepeats.length,
  };
}

export type BillingItemWire = Schemas["BillingItem"];

const WIRE_CATEGORY: Record<Schemas["VatCategory"], T.VatCategory> = {
  standard: "S",
  zeroRated: "Z",
  exempt: "E",
  reverseCharge: "AE",
  outsideScope: "O",
  intraCommunity: "E",
  freeExport: "E",
};

const TO_WIRE_CATEGORY: Record<T.VatCategory, Schemas["VatCategory"]> = {
  S: "standard",
  Z: "zeroRated",
  E: "exempt",
  AE: "reverseCharge",
  O: "outsideScope",
};

/** A query parameter binds to the enum's own name, which is not how a JSON body spells it. */
export function billingItemCategoryQuery(category: T.VatCategory): string {
  const wire = TO_WIRE_CATEGORY[category];
  return wire.charAt(0).toUpperCase() + wire.slice(1);
}

export function toBillingItem(wire: BillingItemWire): T.BillingItem {
  return {
    id: wire.id,
    name: wire.name,
    description: wire.description ?? "",
    hsCode: wire.hsCode ?? "",
    // A price isn't an amount: it keeps the decimals it was entered with.
    unitPrice: String(Number(wire.unitPrice)),
    incomeAccountId: wire.incomeAccountId,
    category: WIRE_CATEGORY[wire.category],
    ratePct: wire.rate == null ? null : String(Number(wire.rate)),
    exemptionReason: wire.exemptionReason ?? "",
    usedOnDocuments: Number(wire.usedOnDocuments),
  };
}

export function fromBillingItemForm(form: T.BillingItemForm) {
  return {
    name: form.name,
    description: form.description || null,
    hsCode: form.hsCode || null,
    unitPrice: form.unitPrice,
    incomeAccountId: form.incomeAccountId,
    category: TO_WIRE_CATEGORY[form.category],
    rate: form.ratePct,
    exemptionReason: form.exemptionReason || null,
  };
}

export function toSupplierReconciliation(
  wire: SupplierReconciliationWire,
): T.SupplierReconciliation {
  return {
    id: wire.id,
    statementDate: wire.statementDate,
    statementBalance: money(wire.statementBalance),
    bookBalance: money(wire.bookBalance),
    difference: money(wire.difference),
    recordedAt: wire.recordedAt,
  };
}

export function toPartyStatement(wire: PartyStatementWire): T.PartyStatement {
  return {
    from: wire.from,
    to: wire.to,
    opening: money(wire.opening),
    closing: money(wire.closing),
    totalDebit: money(wire.totalDebit),
    totalCredit: money(wire.totalCredit),
    allLines: Number(wire.allLines),
    lines: wire.lines.map((line) => ({
      journalId: line.journalId,
      documentId: line.documentId,
      date: line.date,
      documentType: line.documentType,
      number: line.documentNumber,
      details: line.details ?? null,
      debit: money(line.debit),
      credit: money(line.credit),
      balance: money(line.balance),
    })),
  };
}

/** How the screens name a party's role, as the API's query spells it. */
export const PARTY_ROLE: Record<T.Party["kind"], string> = {
  customer: "Customer",
  supplier: "Supplier",
  employee: "Employee",
};

export function toPartySummary(wire: PartySummaryWire, kind: T.Party["kind"]): T.Party {
  return {
    id: wire.id,
    kind,
    name: wire.tradingName ?? wire.legalName,
    pan: wire.taxId ?? undefined,
  };
}

function kindOf(roles: string): T.Party["kind"] {
  if (roles.includes("Customer") || roles.includes("customer")) return "customer";
  if (roles.includes("Supplier") || roles.includes("supplier")) return "supplier";
  return "employee";
}

export function toParty(wire: PartyWire): T.Party {
  const address = [wire.addressLine1, wire.addressLine2, wire.city].filter(Boolean).join(", ");
  return {
    id: wire.id,
    kind: kindOf(String(wire.roles)),
    name: wire.tradingName ?? wire.legalName,
    pan: wire.taxId ?? undefined,
    phone: wire.contactPhone ?? undefined,
    email: wire.contactEmail ?? undefined,
    address: address === "" ? undefined : address,
  };
}

export function toPartyForm(wire: PartyWire): T.PartyForm {
  return {
    kind: kindOf(String(wire.roles)),
    legalName: wire.legalName,
    tradingName: wire.tradingName ?? "",
    pan: wire.taxId ?? "",
    vatRegistered: wire.taxRegistered,
    legalRegistration: wire.legalRegistration ?? "",
    street: wire.addressLine1 ?? "",
    city: wire.city ?? "",
    phone: wire.contactPhone ?? "",
    email: wire.contactEmail ?? "",
    // The API holds four decimal places, and the form takes two.
    creditLimit: wire.creditLimit == null ? "" : money(wire.creditLimit),
    paymentTermsDays: wire.paymentTermsDays == null ? "" : String(wire.paymentTermsDays),
    withholdingCode: wire.withholdingCode ?? "",
  };
}

/** The fields this form owns. An empty one is absent, not an empty string on a filed document. */
export function fromPartyForm(form: T.PartyForm) {
  const orNull = (value: string) => (value.trim() === "" ? null : value.trim());
  return {
    legalName: form.legalName.trim(),
    tradingName: orNull(form.tradingName),
    taxId: orNull(form.pan),
    taxRegistered: form.vatRegistered,
    legalRegistration: orNull(form.legalRegistration),
    addressLine1: orNull(form.street),
    city: orNull(form.city),
    contactPhone: orNull(form.phone),
    contactEmail: orNull(form.email),
    creditLimit: orNull(form.creditLimit),
    paymentTermsDays: form.paymentTermsDays === "" ? null : Number(form.paymentTermsDays),
    withholdingCode: orNull(form.withholdingCode),
  };
}

export function toAccount(a: AccountWire): T.Account {
  return {
    id: a.id,
    code: a.code,
    name: a.name,
    group: ACCOUNT_GROUP[a.type],
    isActive: a.isActive,
    hasRole: a.hasRole,
    subtype: (a.subtype ?? null) as T.AccountSubtype | null,
    statementLine: a.statementLine ?? null,
    normalBalance: (a.normalBalance ?? null) as T.Account["normalBalance"],
    isRequired: a.isRequired,
    requiredReason: a.requiredReason ?? null,
    postingRule: a.postingRule as T.PostingRule,
    source: a.source,
    ...(a.balance === undefined || a.balance === null ? {} : { balance: String(a.balance) }),
  };
}

export function toAccounts(wire: readonly AccountWire[]): T.Account[] {
  return wire.map(toAccount);
}

export function toTemplate(wire: Schemas["CoaTemplateSummary"]): T.CoaTemplate {
  return {
    code: wire.code,
    version: Number(wire.version),
    name: wire.name,
    description: wire.description,
    isDefault: wire.isDefault,
    accountCount: Number(wire.accountCount),
    region: wire.region ?? null,
    edition: wire.edition ?? null,
    basis: wire.basis ?? null,
  };
}

export function toTemplateDetail(wire: Schemas["CoaTemplateDetail"]): T.CoaTemplateDetail {
  return {
    template: toTemplate(wire.template),
    accounts: wire.accounts.map((a) => ({
      code: a.code,
      name: a.name,
      group: ACCOUNT_GROUP[a.type],
      subtype: (a.subtype ?? null) as T.AccountSubtype | null,
      statementLine: a.statementLine ?? null,
      normalBalance: (a.normalBalance ?? null) as T.CoaTemplateAccount["normalBalance"],
    })),
  };
}

export function toReference(wire: Schemas["AccountReference"]): T.AccountReference {
  return {
    accountId: wire.accountId,
    code: wire.code,
    name: wire.name,
    kind: wire.kind,
    uses: Number(wire.uses),
  };
}

/** The wire's class for a screen's group. */
export const accountType = (group: T.AccountGroup): Schemas["AccountType"] =>
  (Object.entries(ACCOUNT_GROUP).find(([, g]) => g === group)?.[0] ??
    "asset") as Schemas["AccountType"];

export function toBankAccount(account: BankAccountWire): T.BankAccount {
  return {
    id: account.id,
    accountId: account.ledgerAccountId,
    name: account.name,
    number: account.accountNumber ?? undefined,
    kind: "bank",
    currency: account.currencyCode,
    balance: String(account.balance),
  };
}

export function toBranding(wire: Schemas["DocumentBranding"]): T.DocumentBranding {
  return {
    invoiceTemplate: wire.invoiceTemplate as T.BrandingTemplate,
    receiptTemplate: wire.receiptTemplate as T.BrandingTemplate,
    colour: wire.colour as T.BrandingColour,
    receiptColour: (wire.receiptColour ?? null) as T.BrandingColour | null,
    showLogo: wire.showLogo,
    showTaxNumber: wire.showTaxNumber,
    showContact: wire.showContact,
    showPaymentDetails: wire.showPaymentDetails,
    showAmountInWords: wire.showAmountInWords,
    showSignature: wire.showSignature,
    showDiscount: wire.showDiscount,
    logoData: wire.logoData ?? null,
  };
}

export function toCompany(wire: CompanyWire): T.CompanyProfile {
  return {
    contactPhone: wire.contactPhone ?? undefined,
    contactEmail: wire.contactEmail ?? undefined,
    legalName: wire.legalName,
    tradeName: wire.tradingName ?? "",
    pan: wire.taxId ?? "",
    vatRegistered: wire.taxRegistered,
    country: wire.countryCode,
    currency: wire.homeCurrency,
    address: {
      street: wire.addressLine1 ?? "",
      municipality: wire.city ?? "",
      ward: wire.addressLine2 ?? "",
      district: "",
      province: wire.countrySubdivision ?? "",
      postalCode: wire.postalZone ?? "",
    },
  };
}

/** What `PUT /settings/company` takes. An empty field is absent, not an empty string on a filed document. */
export function fromCompany(profile: T.CompanyProfile) {
  const orNull = (value: string | undefined) =>
    value === undefined || value === "" ? null : value;
  return {
    legalName: profile.legalName,
    countryCode: profile.country ?? "NP",
    homeCurrency: profile.currency ?? "NPR",
    tradingName: orNull(profile.tradeName),
    taxId: orNull(profile.pan),
    taxRegistered: profile.vatRegistered,
    contactPhone: orNull(profile.contactPhone),
    contactEmail: orNull(profile.contactEmail),
    addressLine1: orNull(profile.address.street),
    addressLine2: orNull(profile.address.ward),
    city: orNull(profile.address.municipality),
    postalZone: orNull(profile.address.postalCode),
    countrySubdivision: orNull(profile.address.province),
  };
}

export function toFiscalYears(wire: readonly FiscalYearWire[], today: T.IsoDate): T.FiscalYear[] {
  return wire.map((y) => ({
    id: y.id,
    label: `FY ${y.code}`,
    start: y.startsOn,
    end: y.endsOn,
    status: YEAR_STATUS[y.status],
    isCurrent: y.startsOn <= today && today <= y.endsOn,
  }));
}

export function toNumbering(wire: NumberingWire): T.NumberingSettings {
  return {
    includeFiscalYearCode: wire.includeFiscalYearCode,
    fiscalYearCode: wire.fiscalYearCode ?? null,
    firstYear: wire.firstYear,
    sequences: wire.sequences.map((s) => ({
      code: s.code,
      label: s.label,
      prefix: s.prefix,
      defaultPrefix: s.defaultPrefix,
      next: Number(s.nextNumber),
      lock: (s.lock ?? null) as T.NextNumberLock | null,
    })),
  };
}

/** "13.0000" → "13": the column's scale, not a fact about the rate. */
function plainRate(rate: string): string {
  return rate.includes(".") ? rate.replace(/\.?0+$/, "") : rate;
}

export function toTaxCodes(
  wire: readonly TaxCodeWire[],
  roles: Readonly<Record<string, string>> = {},
): T.TaxCode[] {
  return wire.map((t) => {
    const ratePct = t.ratePercent === null ? "" : plainRate(String(t.ratePercent));
    return {
      id: t.id,
      code: t.code,
      name: t.name,
      // EN 16931's category: standard-rated when it carries a rate, zero-rated or exempt otherwise.
      category: ratePct === "" || Number(ratePct) === 0 ? "Z" : "S",
      ratePct,
      recoverable: t.creditable,
      accountIds: [
        ...new Set([t.outputRole, t.inputRole].flatMap((role) => (role && roles[role]) || [])),
      ],
    };
  });
}

export type TransferPageWire = Schemas["FundTransferPage"];
export type TransferViewWire = Schemas["FundTransferView"];

function amount(value: unknown): T.Money {
  const [whole = "0", fraction = ""] = String(value).split(".");
  return `${whole}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}

export function toTransfer(row: Schemas["FundTransferRow"]): T.FundTransfer {
  return {
    id: row.id,
    number: row.number,
    date: row.transferredOn,
    fromBankAccountId: row.fromBankAccountId,
    fromName: row.fromName,
    toBankAccountId: row.toBankAccountId,
    toName: row.toName,
    reference: row.reference ?? "",
    amount: amount(row.amount),
    status: row.posted ? "approved" : "draft",
  };
}

export function toTransferFromView(
  view: TransferViewWire,
  banks: readonly BankAccountWire[],
): T.FundTransfer {
  const name = (id: string) => banks.find((b) => b.id === id)?.name ?? "";
  return {
    id: view.id,
    number: view.number,
    date: view.transferredOn,
    fromBankAccountId: view.fromBankAccountId,
    fromName: name(view.fromBankAccountId),
    toBankAccountId: view.toBankAccountId,
    toName: name(view.toBankAccountId),
    reference: view.reference ?? "",
    amount: amount(view.amount),
    status: view.status === "posted" ? "approved" : "draft",
  };
}
